/**
 * The presentation-hints bag of a governed map slot (`FmMapSlotProps.presentation`).
 *
 * Every semantic hint a page may give a map travels in this ONE validated
 * bag: adding a hint adds a key here, a validator rule and a renderer branch,
 * never a prop threaded through the map internals. Hints are data. The
 * registered renderer owns what each hint looks like; colours are resolved by
 * the renderer from the one chart palette, so a map can only paint a colour
 * its legend also reads.
 *
 * Which hints each governed slot renders is registered in
 * `components/ui/elements/mapPresentation.validation.ts`; a hint a slot does
 * not render is refused by name, never silently dropped.
 */
import type { FmOperationColorKey } from '@/components/ui/charts/palette';

/** The owner's basemap: MML orthophoto ("Ilmakuva") or MML topographic map ("Kartta"). */
export const FM_MAP_BASEMAP_LENSES = ['ortho', 'topo'] as const;
export type FmMapBasemapLens = (typeof FM_MAP_BASEMAP_LENSES)[number];

/**
 * `muted` reads the same tiles quieter, so the stands drawn over them carry
 * the colour. Absent means the standard tone.
 */
export const FM_MAP_BASEMAP_TONES = ['muted'] as const;
export type FmMapBasemapTone = (typeof FM_MAP_BASEMAP_TONES)[number];

export interface FmMapBasemapHint {
  /** The lens the map opens on. */
  lens?: FmMapBasemapLens;
  tone?: FmMapBasemapTone;
  /** Offer the owner the one-tap "Ilmakuva / Kartta" switch on the map. */
  switcher?: true;
}

/**
 * The stage the map is drawn on.
 * - `hero`: fills its host box (a band background); no frame of its own.
 * - `aerial`: the evidence stage beside a list: the taller workspace stage on
 *   a wide screen and the phone evidence crop on a narrow one.
 * - `phone-evidence`: the compact evidence crop at every width.
 */
export const FM_MAP_STAGES = ['hero', 'aerial', 'phone-evidence'] as const;
export type FmMapStage = (typeof FM_MAP_STAGES)[number];

/**
 * What the first fit frames. An explicit target wins over the property's own
 * fit; there is exactly one fit per target, in the frame the map first has a
 * size, and again only when its container resizes before the owner moves it.
 * - `parcel`: the property.
 * - `selectable`: the features the owner may choose.
 * - `categories`: the categorised features (`featureCategories`).
 * - `selection`: the selected feature; the parcel while nothing is selected.
 */
export const FM_MAP_FIT_TARGETS = ['parcel', 'selectable', 'categories', 'selection'] as const;
export type FmMapFitTarget = (typeof FM_MAP_FIT_TARGETS)[number];

/** Colour families a stand fill may name; each resolves through the chart palette. */
export const FM_MAP_FILL_FAMILIES = ['operation', 'species'] as const;
export type FmMapFillFamily = (typeof FM_MAP_FILL_FAMILIES)[number];

/**
 * A thematic fill of every stand (a lens). A stand without a key reads as
 * "no data" (a dashed outline), never as a guessed class. `quietKeys` are the
 * classes that ask nothing of the owner, drawn as a faint wash.
 */
export type FmMapStandFill =
  | {
      family: 'operation';
      byStandId: Readonly<Record<string, FmOperationColorKey>>;
      quietKeys?: readonly FmOperationColorKey[];
    }
  | {
      family: 'species';
      /** Served species code per stand (Metsäkeskus codes). */
      byStandId: Readonly<Record<string, number>>;
      quietKeys?: readonly number[];
    };

/** Palettes `featureCategories` may be painted in. */
export const FM_MAP_CATEGORY_PALETTES = ['operation'] as const;
export type FmMapCategoryPalette = (typeof FM_MAP_CATEGORY_PALETTES)[number];

/**
 * The features the map is about (a leimikko), each in its category colour.
 * Every other stand stays a quiet outline. Categorised features are what
 * `fitTo: 'categories'` frames and what `reveal` draws in last.
 */
export interface FmMapFeatureCategories {
  palette: FmMapCategoryPalette;
  byFeatureId: Readonly<Record<string, FmOperationColorKey>>;
}

export interface FmMapPresentation {
  basemap?: FmMapBasemapHint;
  stage?: FmMapStage;
  fitTo?: FmMapFitTarget;
  /**
   * Draw the map in on its first load in three waves within 1.2 s: context
   * stands, then the filled or categorised stands, then the selected stand.
   * Reduced motion shows the final state at once.
   */
  reveal?: true;
  standFill?: FmMapStandFill;
  featureCategories?: FmMapFeatureCategories;
  /** One served sentence per feature, shown on hover and focus. */
  featureTooltips?: Readonly<Record<string, string>>;
}

/** Every key of the bag, in documentation order. */
export const FM_MAP_PRESENTATION_HINTS = [
  'basemap',
  'stage',
  'fitTo',
  'reveal',
  'standFill',
  'featureCategories',
  'featureTooltips',
] as const satisfies readonly (keyof FmMapPresentation)[];

/**
 * One renderable capability. A slot registers the capabilities it renders;
 * `basemap.*` and `fitTo:*` are registered per sub-key and per target.
 */
export type FmMapPresentationCapability =
  | 'basemap.lens'
  | 'basemap.tone'
  | 'basemap.switcher'
  | 'stage'
  | `fitTo:${FmMapFitTarget}`
  | 'reveal'
  | 'standFill'
  | 'featureCategories'
  | 'featureTooltips';
